import { motion } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { fadeInUp, staggerContainer } from '@/lib/animations';

export function FairUse() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Navbar />
      <main className="pt-24 pb-16 md:pt-32 md:pb-24">
        <Container size="narrow">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-8"
            >
              Politica de Uso Justo de IA
            </motion.h1>

            <motion.div
              variants={fadeInUp}
              className="prose prose-gray dark:prose-invert max-w-none"
            >
              <p className="text-gray-600 dark:text-slate-400 mb-6">
                Ultima actualizacion: Enero 2026
              </p>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Proposito de esta Politica
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  El plan Pro de Affogato incluye acceso a funcionalidades de inteligencia artificial
                  de manera "ilimitada". Esta politica de uso justo garantiza que todos los usuarios
                  puedan disfrutar de un servicio de calidad, evitando abusos que puedan degradar
                  la experiencia para otros.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Limites de Uso Justo
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  Para mantener la calidad del servicio, aplicamos los siguientes limites razonables:
                </p>

                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full border border-gray-200 dark:border-slate-700 rounded-lg">
                    <thead className="bg-gray-50 dark:bg-slate-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Limite</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Valor</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Proposito</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Por hora</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">30 solicitudes</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Prevenir automatizacion abusiva</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Por dia</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">100 solicitudes</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Uso razonable diario</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Por mes (soft limit)</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">1,000 solicitudes</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Revision si se excede consistentemente</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Input maximo</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">10,000 tokens</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Optimizar procesamiento</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Output maximo</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">4,000 tokens</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-400">Respuestas completas y eficientes</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Uso Normal Esperado
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  Basado en nuestro analisis, un consultor activo tipicamente utiliza entre
                  15 y 30 solicitudes de IA por dia para tareas como:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-slate-400 space-y-2">
                  <li>Generacion de descripciones para archivos de biblioteca</li>
                  <li>Creacion de borradores de propuestas y documentos</li>
                  <li>Analisis de respuestas de diagnosticos</li>
                  <li>Generacion de reportes de portal para clientes</li>
                  <li>Analisis predictivo de proyectos</li>
                </ul>
                <p className="text-gray-600 dark:text-slate-400 mt-4">
                  Los limites establecidos permiten entre 3x y 5x el uso normal, asegurando
                  que incluso en dias de alta actividad tengas suficiente capacidad.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Que Sucede si Excedo los Limites
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  Si alcanzas un limite:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-slate-400 space-y-2">
                  <li><strong>Limite por hora:</strong> Las solicitudes se pausan temporalmente hasta que se reinicie el contador (maximo 60 minutos de espera)</li>
                  <li><strong>Limite diario:</strong> Se notifica y se puede continuar al dia siguiente. No se bloquea el acceso a otras funcionalidades</li>
                  <li><strong>Limite mensual (soft):</strong> Se revisa el patron de uso. Si es uso legitimo, no hay consecuencias. Si detectamos automatizacion o abuso, contactamos al usuario</li>
                </ul>
                <p className="text-gray-600 dark:text-slate-400 mt-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <strong>Importante:</strong> Nunca bloqueamos el acceso completo a tu cuenta ni eliminamos tus datos
                  por exceder los limites de uso justo. Simplemente aplicamos throttling temporal para
                  proteger la calidad del servicio.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Uso Prohibido
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  Los siguientes usos de la IA estan prohibidos y pueden resultar en suspension:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-slate-400 space-y-2">
                  <li>Automatizacion masiva mediante scripts o bots</li>
                  <li>Reventa o redistribucion del servicio de IA a terceros</li>
                  <li>Generacion de contenido ilegal, danino o que viole derechos de terceros</li>
                  <li>Intentos de evadir o manipular los limites de uso</li>
                  <li>Uso de la API de manera no autorizada</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Plan Starter
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  El plan Starter incluye 50 usos de IA por mes como limite fijo (no sujeto a politica
                  de uso justo). Los usos se reinician el primer dia de cada ciclo de facturacion.
                  Si necesitas mas capacidad de IA, considera actualizar al plan Pro.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Cambios en esta Politica
                </h2>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  Podemos ajustar los limites de uso justo para reflejar cambios en costos de
                  infraestructura o patrones de uso. Notificaremos cambios significativos con
                  al menos 30 dias de antelacion.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Contacto
                </h2>
                <p className="text-gray-600 dark:text-slate-400">
                  Si tienes preguntas sobre esta politica o crees que tu uso fue limitado
                  injustamente, contactanos en:{' '}
                  <a
                    href="mailto:hola@affogato.cloud"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    hola@affogato.cloud
                  </a>
                </p>
              </section>
            </motion.div>
          </motion.div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
